import { Button, Grid, makeStyles, Snackbar, Typography } from '@material-ui/core';
import { getRedBook, mergeRedBook } from '../../api/redbook';
import { Alert } from '../../components/Alert/Alert';
import { ConfirmationDialog } from '../../components/Dialogs/ConfirmationDialog';
import Spinner from '../../components/Spinner';
import { MergeResult, RedBookRecord, User, UserRole } from '../../domain/domain';
import MUIDataTable from 'mui-datatables';
import React, { useEffect, useState, useCallback } from 'react';
import { useRouteMatch } from 'react-router-dom';
import { ApplicationState } from '../../reducers';
import { trimString } from '../../utils';

import { compose } from "redux";
import { connect } from "react-redux";
import MergeResultDialog from './MergeResultDialog';
import HourglassSpinner from '../HourglassSpinner';
import NotFound from '../../components/NotFound';
import { SNACKBAR_LONG_AUTO_HIDE_DURATION_MS } from '../../api/common';

const useStyles = makeStyles(_ => ({
    root: {
        maxWidth: '90%',
        marginLeft: 'auto',
        marginRight: 'auto',
        marginTop: 20
    },
}));


type Props = {
    currentUser: User
}

const RedBookPageV2: React.FC<Props> = ({ currentUser }) => {

    const match = useRouteMatch();
    const classes = useStyles();
    const [records, setRecords] = useState<RedBookRecord[]>([]);
    const [selectedRows, setSelectedRows] = useState<any[] | undefined>(undefined);
    const [loading, setLoading] = useState<boolean>(false);
    const [billNumber] = useState<string>((match.params as any).billNumber);
    const [billSection] = useState<string>((match.params as any).billSection);
    const [mergeConfirmationDialogOpen, setMergeConfirmationDialogOpen] = useState<boolean>(false);
    const [snackBarOpen, setSnackBarOpen] = useState<boolean>(false);
    const [snackBarMessage, setSnackBarMessage] = useState<string>("");
    const [snackBarSeverity, setSnackBarSeverity] = useState<"success" | "error">("success");
    const [mergeResultDialogOpen, setMergeResultDialogOpen] = useState<boolean>(false);
    const [mergeResult, setMergeResult] = useState<MergeResult | undefined>(undefined);
    const [mergeLoading, setMergeLoading] = useState<boolean>(false);
    const [mergeWarning, setMergeWarning] = useState<string>("");
    const [searchText, setSearchText] = useState<string>("");


    const sortCompare = (order: string, dataType: 'number' | 'date') => {
        return (a: any, b: any) => {
            let valA = 0, valB = 0;

            if (dataType === 'number') {
                valA = parseInt(a.data, 10) || 0;
                valB = parseInt(b.data, 10) || 0;
            } else if (dataType === 'date') {
                valA = new Date(a.data).getTime() || 0;
                valB = new Date(b.data).getTime() || 0;
            }

            return order === 'asc' ? valA - valB : valB - valA;
        };
    };

    const compareCRS = (a: string, b: string, order: string) => {
        const parseCRS = (str: string) => {
            const [title, article, sectionWithSubsection] = str.split('-');
            const section = sectionWithSubsection?.split('(')[0] || '0';

            return [
                parseInt(title, 10) || 0,
                parseInt(article, 10) || 0,
                parseInt(section, 10) || 0
            ];
        };

        const partsA = parseCRS(a);
        const partsB = parseCRS(b);

        for (let i = 0; i < partsA.length; i++) {
            if (partsA[i] !== partsB[i]) {
                return order === 'asc' ? partsA[i] - partsB[i] : partsB[i] - partsA[i];
            }
        }

        return 0;
    };

    const columns = [
        {
            name: 'crs',
            label: 'C.R.S',
            options: {
                sortCompare: (order: string) => {
                    return (a: any, b: any) => {
                        return compareCRS(a.data, b.data, order);
                    };
                },
            }
        },
        {
            name: 'change',
            label: 'Change'
        },
        {
            name: 'billNo',
            label: 'Bill No.'
        },
        {
            name: 'billSection',
            label: 'Bill §',
            options: {
                sortCompare: (order: string) => sortCompare(order, 'number')
            }
        },
        {
            name: 'footnote',
            label: 'Footnote'
        },
        {
            name: 'chapter',
            label: 'Chapter',
            options: {
                sortCompare: (order: string) => sortCompare(order, 'number')
            }
        },
        {
            name: 'page',
            label: 'Page',
            options: {
                sortCompare: (order: string) => sortCompare(order, 'number')
            }
        },
        {
            name: 'effectiveDate',
            label: 'Effective Date',
            options: {
                sortCompare: (order: string) => sortCompare(order, 'date')
            }
        }
    ];


    const loadRecords = useCallback(() => {
        setLoading(true);
        getRedBook(trimString(billNumber), trimString(billSection)).then(response => {
            setRecords(response.data);
        }).catch(error => {
            console.error(error);
        }).finally(() => {
            setLoading(false);
        })
    }, [billNumber, billSection]);

    useEffect(() => {
        loadRecords();
    }, [loadRecords]);

    const handleSearchChange = (newSearchText: string) => {
        if (newSearchText !== searchText) {
            setSelectedRows([]);
        }
        setSearchText(newSearchText);
    };

    const merge = () => {
        if (selectedRows && selectedRows.length > 0) {
            const selectedRecords = selectedRows.map(rowIndex => records[rowIndex]);
            const hasMissingEffectiveDate = selectedRecords.some(record => !record.effectiveDate);

            if (hasMissingEffectiveDate) {
                setMergeWarning("Warning: Some selected rows do not have an effective date.");
            } else {
                setMergeWarning("");
            }
        } 
        setMergeConfirmationDialogOpen(true);
    }

    const getHeader = () => {
        return (
            <Grid container direction='row' className="flex-gap-1">
                {(currentUser.userRole === UserRole.PUBLICATIONS_TEAM || currentUser.userRole === UserRole.ADMINISTRATOR) &&
                    <Grid item>
                        <Button variant='outlined' disabled={!selectedRows || selectedRows.length === 0} color='primary' onClick={() => merge()}>Merge</Button>
                    </Grid>
                }
            </Grid>
        )
    }

    if (currentUser.userRole === UserRole.NON_PUBLICATIONS_TEAM) {
        return <NotFound />
    }

    return (
        <>
            {mergeResult &&
                <MergeResultDialog open={mergeResultDialogOpen} mergeResult={mergeResult} onClose={() => {
                    setMergeResultDialogOpen(false);
                }} />
            }
            <Snackbar open={snackBarOpen} anchorOrigin={{ vertical: 'top', horizontal: 'center' }} autoHideDuration={SNACKBAR_LONG_AUTO_HIDE_DURATION_MS} onClose={() => { setSnackBarOpen(false) }}>
                <Alert onClose={() => { setSnackBarOpen(false) }} severity={snackBarSeverity}>
                    {snackBarMessage}
                </Alert>
            </Snackbar>
            <ConfirmationDialog
                open={mergeConfirmationDialogOpen}
                title="Merge"
                loading={mergeLoading}
                loadingText={<HourglassSpinner message="Merge in progress, please wait..." />}
                contentText={`Are you sure you want to perform the merge for the ${selectedRows?.length || 0} selected row(s) in the search results?`}
                warningText={mergeWarning}
                handleNo={() => {
                    setMergeConfirmationDialogOpen(false);
                    setMergeLoading(false);
                }}
                handleYes={() => {
                    setMergeLoading(true);
                    setMergeResult(undefined);
                    let selectedRecords = selectedRows && selectedRows.length > 0 ? selectedRows.map(rowIndex => records[rowIndex]) : [];
                    mergeRedBook(billNumber, billSection, selectedRecords).then(response => {
                        setMergeResultDialogOpen(true);
                        setMergeResult(response.data);
                    }).catch(error => {
                        console.error(error);
                        setSnackBarSeverity("error");
                        setSnackBarMessage("Internal server errror. Please try again later.");
                        setSnackBarOpen(true);
                        setMergeLoading(false);
                    }).finally(() => {
                        setMergeLoading(false);
                        setMergeConfirmationDialogOpen(false);
                        setSelectedRows([]);
                    })
                }}
            />
            <Grid container direction='column' className={`${classes.root} flex-gap-1`}>
                <Grid item>
                    <Typography variant='h1'>Red Book (Current Year Only)</Typography>
                </Grid>
                <Grid item>
                    {getHeader()}
                </Grid>
                <Grid item>
                    {loading &&
                        <Spinner />
                    }
                    {!loading &&
                        <MUIDataTable
                            title={""}
                            columns={columns}
                            data={records}
                            options={{
                                filter: false,
                                search: true,
                                searchAlwaysOpen: true,
                                print: false,
                                download: false,
                                viewColumns: false,
                                selectableRowsHeader: true,
                                selectableRows: 'multiple',
                                selectToolbarPlacement: 'above',
                                customToolbarSelect: () => null,
                                rowsSelected: selectedRows,
                                onRowSelectionChange: (rowsSelectedData, allRows, rowsSelected) => {
                                    setSelectedRows(rowsSelected);
                                },
                                onSearchChange: (searchText) => {
                                    handleSearchChange(searchText || "");
                                },
                                elevation: 0,
                                rowsPerPageOptions: [10, 50, 100, 200],
                                setTableProps: () => {
                                    return {
                                        size: "small",
                                        padding: "1px"
                                    }
                                }
                            }}
                        />
                    }
                </Grid>
            </Grid>
        </>
    )
}

const mapStateToProps = (state: ApplicationState) => {
    return {
        currentUser: state.user.currentUser
    };
};

const mapDispatchToProps = (dispatch: any) => {
    return { dispatch };
}

export default compose<any>(
    connect(mapStateToProps, mapDispatchToProps)
)(RedBookPageV2);
